// Pairing analysis for the case dashboard, from the case's pairing_event rows
// ({ id, ts, session_id, type, mac, player_id, rssi, detail }). `detail` is the
// whole event as JSON text. `timing` rows carry per-step seconds:
// { sighted_to_held, held_to_cmd, cmd_queued, assign, total }.
import { parseUtc } from "./caseDashboard";

export const TIMING_STEPS = [
  "sighted_to_held",
  "held_to_cmd",
  "cmd_queued",
  "assign",
];

export function parseDetail(row) {
  if (!row?.detail) return {};
  if (typeof row.detail === "object") return row.detail;
  try {
    return JSON.parse(row.detail) || {};
  } catch {
    return {};
  }
}

export function median(values) {
  const v = values.filter((x) => typeof x === "number" && !Number.isNaN(x));
  if (!v.length) return null;
  const s = [...v].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/**
 * Time-to-assign from `timing` events:
 * { count, median, slowest: { mac, total, ts } | null, steps: { step: median } }.
 */
export function timingStats(events) {
  const timings = (events || [])
    .filter((e) => e.type === "timing")
    .map((e) => ({ mac: e.mac, ts: e.ts, ...parseDetail(e) }))
    .filter((t) => typeof t.total === "number");
  const steps = {};
  for (const step of TIMING_STEPS)
    steps[step] = median(timings.map((t) => t[step]));
  const slowest = timings.reduce(
    (worst, t) => (!worst || t.total > worst.total ? t : worst),
    null
  );
  return {
    count: timings.length,
    median: median(timings.map((t) => t.total)),
    slowest: slowest
      ? { mac: slowest.mac, total: slowest.total, ts: slowest.ts }
      : null,
    steps,
  };
}

const OUTCOME_EVENTS = { assigned: "assigned", assign_failed: "failed" };

/**
 * One timeline per wearable, newest activity first:
 * [{ mac, name, events: [{ ts, type, detail }], outcome, lastTs }].
 * outcome is "assigned" | "failed" | "pending" from the latest outcome event.
 */
export function wearableTimelines(events) {
  const byMac = new Map();
  for (const e of events || []) {
    if (!e.mac) continue;
    const mac = String(e.mac).toUpperCase();
    let t = byMac.get(mac);
    if (!t) {
      t = { mac, name: null, events: [], outcome: "pending", lastTs: null };
      byMac.set(mac, t);
    }
    const detail = parseDetail(e);
    if (!t.name && detail.name) t.name = detail.name;
    t.events.push({ ts: e.ts, type: e.type, detail });
    if (OUTCOME_EVENTS[e.type]) t.outcome = OUTCOME_EVENTS[e.type];
    t.lastTs = e.ts;
  }
  const time = (ts) => parseUtc(ts)?.getTime() ?? 0;
  return [...byMac.values()].sort((a, b) => time(b.lastTs) - time(a.lastTs));
}

/** Failed assigns and warnings, newest first: [{ ts, mac, type, message }]. */
export function pairingProblems(events) {
  return (events || [])
    .filter((e) => e.type === "assign_failed" || e.type === "assign_warning")
    .map((e) => {
      const d = parseDetail(e);
      return {
        ts: e.ts,
        mac: e.mac,
        type: e.type,
        message: d.error || d.warning || "",
      };
    })
    .reverse();
}

/**
 * Who each wearable was assigned to, from the phone's assign commands
 * (`cmd:assign` carries player_name): { MAC: name }. Latest wins.
 */
export function playerNamesByMac(events) {
  const names = {};
  for (const e of events || []) {
    if (e.type !== "cmd:assign" || !e.mac) continue;
    const d = parseDetail(e);
    if (d.player_name) names[String(e.mac).toUpperCase()] = d.player_name;
  }
  return names;
}

/** Every timed pairing, oldest first: [{ mac, ts, total, ...steps, player }]. */
export function pairingRuns(events) {
  const names = playerNamesByMac(events);
  return (events || [])
    .filter((e) => e.type === "timing")
    .map((e) => {
      const d = parseDetail(e);
      const mac = e.mac ? String(e.mac).toUpperCase() : null;
      return { mac, ts: e.ts, player: (mac && names[mac]) || null, ...d };
    })
    .filter((r) => typeof r.total === "number");
}

/** Share of finished pairings that succeeded, 0..1, or null if none ended. */
export function successRate(events) {
  let ok = 0;
  let failed = 0;
  for (const e of events || []) {
    if (e.type === "assigned") ok += 1;
    if (e.type === "assign_failed") failed += 1;
  }
  return ok + failed ? ok / (ok + failed) : null;
}
